import { useState, useEffect } from 'react';
import { auth, db } from '../services/firebase';
import { 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile as updateFirebaseProfile
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { UserProfile } from '../types';
import { generateAvatarUrl } from '../utils/helpers';

export const useAuth = () => {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    console.log('🔥 useAuth: Starting auth listener...');
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔥 useAuth: Auth state changed - User:', firebaseUser ? firebaseUser.email : 'No user');
      
      setUser(firebaseUser);
      
      if (firebaseUser) {
        try {
          console.log('🔥 useAuth: Fetching profile for user:', firebaseUser.uid);
          const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
          
          if (userDoc.exists()) {
            const profileData = userDoc.data() as UserProfile;
            console.log('🔥 useAuth: Profile found:', profileData.email);
            console.log('🔥 useAuth: Profile hasSetupProfile:', profileData.hasSetupProfile);
            
            setProfile(profileData);
          } else {
            console.log('🔥 useAuth: NO PROFILE FOUND - User needs to go through AuthScreen');
            setProfile(null);
          }
        } catch (error) {
          console.error("🔥 useAuth: Error fetching profile:", error);
          setProfile(null);
        }
      } else {
        console.log('🔥 useAuth: No user - showing AuthScreen');
        setProfile(null);
      }
      
      setLoadingProfile(false);
    });

    return unsubscribe;
  }, []);

  // Special function for AuthScreen to create profile after registration
  const createUserProfile = async (firebaseUser: any, additionalData: any = {}) => {
    try {
      console.log('🔥 useAuth: Creating new profile for:', firebaseUser.email);
      
      const newProfile: UserProfile = {
        id: firebaseUser.uid,
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        displayName: firebaseUser.displayName || additionalData.displayName || 'User',
        handle: `@${(firebaseUser.displayName || additionalData.displayName || 'user').replace(/\s/g, '').toLowerCase()}`,
        bio: 'Welcome to Scoop! Ready to discover your style.',
        avatar: firebaseUser.photoURL || generateAvatarUrl(firebaseUser.displayName || 'User'),
        
        // ✅ FIX: Save phone number from additionalData
        phoneNumber: additionalData.phoneNumber || '',
        phone: additionalData.phoneNumber || '',
        
        cred: 100,
        followers: 0,
        following: 0,
        stylePreferences: [],
        wishlist: [],
        savedProducts: [],
        savedPosts: [],
        followingUsers: [],
        blockedUsers: [],
        friendRequests: [],
        accountType: 'user',
        hasSetupProfile: false,
        settings: {
          notifications: true,
          privacy: false,
          currency: 'ZAR',
          theme: 'dark',
          language: 'en',
          marketingEmails: true
        },
        shippingAddresses: [],
        paymentMethods: [],
        membership: {
          tier: 'free'
        },
        ...additionalData
      };
      
      await setDoc(doc(db, "users", firebaseUser.uid), {
        ...newProfile,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log('🔥 useAuth: Profile created successfully');
      setProfile(newProfile);
      
      return newProfile;
    } catch (error) {
      console.error("🔥 useAuth: Error creating profile:", error);
      throw error;
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>): Promise<void> => {
    if (!user) return; 
    
    try {
      const userRef = doc(db, "users", user.uid);
      
      if (updates.displayName && profile?.displayName !== updates.displayName) {
        await updateFirebaseProfile(user, {
          displayName: updates.displayName
        });
      }
      
      await setDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      const updatedProfile = profile 
        ? { ...profile, ...updates } 
        : { id: user.uid, uid: user.uid, ...updates } as UserProfile;
        
      setProfile(updatedProfile);
      
      console.log('🔥 useAuth: Profile updated:', updates);
    } catch (error) {
      console.error("🔥 useAuth: Error updating profile:", error);
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      console.log('🔥 useAuth: Attempting login for:', email);
      const result = await signInWithEmailAndPassword(auth, email, password);
      console.log('🔥 useAuth: Login successful');
      return result.user;
    } catch (error) {
      console.error('🔥 useAuth: Login error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string, displayName: string) => {
    try {
      console.log('🔥 useAuth: Attempting registration for:', email);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateFirebaseProfile(result.user, { displayName });
      console.log('🔥 useAuth: Registration successful');
      return result.user;
    } catch (error) {
      console.error('🔥 useAuth: Registration error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await auth.signOut();
      console.log('🔥 useAuth: Logged out successfully');
    } catch (error) {
      console.error('🔥 useAuth: Logout error:', error);
    }
  };

  return {
    user,
    profile,
    loadingProfile,
    createUserProfile,
    updateProfile,
    login,
    register,
    logout
  };
};